import React, { useEffect, useState } from 'react';
import { Task } from '@spike/task-model';
import { completeTaskThunk, TasksStatus, uncompleteTaskThunk } from '@spike/tasks-action';
import styles from './TaskCard.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faPencil } from '@fortawesome/pro-regular-svg-icons';
import { TASK_TYPE_OPTIONS } from 'components/Tasks/taskTypes';
import { useApiClientWrapper, useMasterData, useTimeZone } from 'hooks';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import Spinner from 'components/UI/V3/Spinner';
import { GenericDeleteModal } from 'components/UI/V3/Modals/DeleteModal';
import { AppointmentOrderItem } from '@spike/order-model';
import { NewBookingsStatus, singleRescheduleThunk } from '@spike/new-bookings-action';
import useNonInitialEffect from '@versiondos/hooks';
import { reloadOrderThunk } from '@spike/orders-action';

interface TaskCardProps {
    appointmentItem: AppointmentOrderItem;
    task: Task;
    tasks: Task[];
    onEdit: (task: Task) => void;
    readOnly?: boolean;
}

const byPrefixAndName = {
    fass: {
        'rotate-left': faRotateLeft
    }
};

const getLegacyFeedingTaskType = (feeding?: Task['feeding']): number | undefined =>
    (feeding as { taskType?: number } | undefined)?.taskType;

const formatMomentLabel = (momentTypeId?: number): string => {
    switch (momentTypeId) {
        case 1:
            return 'Morning';
        case 2:
            return 'Afternoon';
        default:
            return 'Evening';
    }
};

export const TaskCard: React.FC<TaskCardProps> = ({ appointmentItem, task, tasks, onEdit, readOnly }) => {
    const masterdata = useMasterData();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const taskStatus = useSelector((state: RootState) => state.tasks.status);
    const timezone = useTimeZone();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingActions, setIsLoadingActions] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isReadOnly = readOnly ?? false;

    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SingleRescheduling:
                setIsLoadingActions(true);
                break;
            case NewBookingsStatus.SingleRescheduleSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                setShowDeleteModal(false);
                setIsLoadingActions(false);
                break;
            case NewBookingsStatus.Error:
                setShowDeleteModal(false);
                setIsLoadingActions(false);
        }
    }, [newBookingsStatus]);

    useEffect(() => {
        switch (taskStatus) {
            case TasksStatus.Completing:
            case TasksStatus.Uncompleting:
                setIsLoading(true);
                break;
            case TasksStatus.CompleteSuccess:
            case TasksStatus.UncompleteSuccess:
            case TasksStatus.Error:
                setIsLoading(false);
                break;
        }
    }, [taskStatus]);

    useNonInitialEffect(() => {
        switch (taskStatus) {
            case TasksStatus.CompleteSuccess:
            case TasksStatus.UncompleteSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
        }
    }, [taskStatus]);

    const isCompleted = task.status === 'completed' || !!task.completed;

    const getTaskType = (): 'feeding' | 'medication' | 'hydration' | 'relief' | undefined => {
        if (task.hydration || getLegacyFeedingTaskType(task.feeding) === 3) {
            return 'hydration';
        }

        if (task.relief || getLegacyFeedingTaskType(task.feeding) === 4) {
            return 'relief';
        }

        if (task.medication && Object.keys(task.medication).length > 0) {
            return 'medication';
        }

        if (task.feeding && Object.keys(task.feeding).length > 0) {
            return 'feeding';
        }

        return undefined;
    };

    const getReliefMomentLabel = (): string | undefined => {
        if (task.relief?.reliefEntry) {
            const { morning, afternoon, evening } = task.relief.reliefEntry;

            if (morning) return 'Morning';
            if (afternoon) return 'Afternoon';
            if (evening) return 'Evening';

            return undefined;
        }

        if (getLegacyFeedingTaskType(task.feeding) === 4) {
            return formatMomentLabel(task.feeding?.feedingMomentTypeId);
        }

        return undefined;
    };

    const getTaskIcon = () => {
        const taskType = getTaskType();
        if (!taskType) return undefined;

        const option = TASK_TYPE_OPTIONS.find(o => o.id === taskType);
        if (!option) return undefined;

        return (
            <div
                className={styles.taskIconContainer}
                style={{ background: option.color }}
            >
                <FontAwesomeIcon
                    icon={option.icon}
                    color="#fff"
                />
            </div>
        );
    };

    const getTaskTitle = () => {
        const taskType = getTaskType();

        switch (taskType) {
            case 'feeding':
                const foodType = masterdata.foodTypes.find(ft => ft.id === task.feeding?.foodTypeId)?.name;
                const quantity = task.feeding?.foodQuantity;
                const unit = masterdata.foodQuantityUnits.find(fq => fq.id === task.feeding?.foodQuantityUnitId)?.name;
                return `${foodType} • ${quantity} ${unit}`;
            case 'medication':
                return task.medication?.medicationName;
            case 'hydration':
                return 'Hydration';
            case 'relief':
                return 'Relief';
            default:
                return 'Task';
        }
    };

    const getTaskSubtitle = () => {
        const taskType = getTaskType();

        switch (taskType) {
            case 'feeding':
                const provisionerType = masterdata.foodProvisionTypes.find(
                    fpt => fpt.id === task.feeding?.foodProvisionerTypeId
                )?.name;
                return provisionerType;
            default:
                return null;
        }
    };

    const getTaskInstructions = () => {
        const taskType = getTaskType();

        switch (taskType) {
            case 'feeding':
                return {
                    title: 'Feeding Instructions',
                    content: task.feeding?.feedingInstructions
                };
            case 'medication':
                const method = masterdata.medicationMethodTypes.find(
                    mmt => mmt.id === task.medication?.medicationMethodId
                )?.name;
                const dosage = task.medication?.dosageAmount
                    ? `${task.medication.dosageAmount} ${
                          masterdata.medicationDosageUnits.find(mdu => mdu.id === task.medication?.dosageUnitId)?.name
                      }`
                    : undefined;
                return {
                    title: 'Medical Instructions',
                    content: task.medication?.medicationInstructions,
                    leftColumn: {
                        title: method,
                        content: dosage
                    }
                };
            case 'hydration':
                return {
                    title: 'Task Note',
                    content: task.hydration?.note ?? task.feeding?.feedingInstructions ?? ''
                };
            case 'relief':
                return {
                    title: 'Task Note',
                    content: task.relief?.note ?? task.feeding?.feedingInstructions ?? ''
                };
            default:
                return null;
        }
    };

    const getTaskTime = () => {
        const taskType = getTaskType();

        if (taskType === 'feeding') {
            return formatMomentLabel(task.feeding?.feedingMomentTypeId);
        }

        if (taskType === 'relief') {
            return getReliefMomentLabel() ?? '';
        }

        if (taskType === 'hydration') {
            return task.taskDate?.clone()?.tz(timezone)?.format('h:mma').toLowerCase() ?? '';
        }

        if (taskType === 'medication') {
            return task.taskDate?.clone()?.tz(timezone)?.format('hh:mm A') ?? '';
        }

        return '';
    };

    const handleCompleteTask = () => {
        if (isReadOnly) return;
        if (isCompleted) {
            dispatch(uncompleteTaskThunk(apiClientWrapper, task.id!));
        } else {
            dispatch(completeTaskThunk(apiClientWrapper, task.id!));
        }
    };

    const deleteTask = () => {
        if (isReadOnly) return;
        const taskToDelete: Task = {
            id: task.id,
            destroy: true
        };
        let tasksFormatted: Task[] = [...tasks];
        tasksFormatted = tasksFormatted.filter(t => t.id !== task.id);
        tasksFormatted.push(taskToDelete);

        dispatch(
            singleRescheduleThunk(
                apiClientWrapper,
                appointmentItem?.appointment.bookingId!,
                appointmentItem?.appointment.id!,
                appointmentItem?.appointment.duration.from.clone(),
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                false,
                undefined,
                tasksFormatted
            )
        );
    };

    const instructions = getTaskInstructions();
    const taskType = getTaskType();

    return (
        <div className={styles.taskCard}>
            <div className={styles.timeBanner}>
                <span className={styles.taskTime}>{getTaskTime()}</span>
            </div>

            <div className={styles.taskContent}>
                <div className={styles.taskHeader}>
                    <div className={styles.taskMain}>
                        {getTaskIcon()}
                        <div className={styles.taskInfo}>
                            <div className={styles.taskTitle}>{getTaskTitle()}</div>
                            {task.feeding?.feedingBrand && (
                                <div className={styles.taskTitle}>{task.feeding?.feedingBrand}</div>
                            )}
                            {getTaskSubtitle() && <div className={styles.taskSubtitle}>{getTaskSubtitle()}</div>}
                        </div>
                    </div>
                    <div className={styles.icons}>
                        {isLoading ? (
                            <Spinner size={24} />
                        ) : (
                            <div className={styles.iconsContainer}>
                                {!isReadOnly && (
                                    <>
                                        <div className={styles.iconContainer}>
                                            <FontAwesomeIcon
                                                icon={faTrashCan}
                                                color="#000000"
                                                className={styles.icon}
                                                onClick={() => setShowDeleteModal(true)}
                                            />
                                        </div>
                                        <div className={styles.iconContainer}>
                                            <FontAwesomeIcon
                                                icon={faPencil}
                                                color="#000000"
                                                className={styles.icon}
                                                onClick={() => onEdit(task)}
                                            />
                                        </div>
                                        {isCompleted ? (
                                            <div className={styles.iconContainer}>
                                                <FontAwesomeIcon
                                                    icon={byPrefixAndName.fass['rotate-left']}
                                                    color="#000000"
                                                    className={styles.icon}
                                                    onClick={handleCompleteTask}
                                                />
                                            </div>
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                color="#92B4A7"
                                                className={styles.completeTaskIcon}
                                                onClick={handleCompleteTask}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {instructions && (
                    <>
                        <div className={styles.separator}></div>
                        <div className={styles.taskInstructions}>
                            {taskType === 'medication' ? (
                                <div className={styles.medicationInstructions}>
                                    <div className={styles.medicationLeft}>
                                        <div className={styles.medicationLeftTitle}>
                                            {instructions.leftColumn?.title}
                                        </div>
                                        <div className={styles.medicationLeftContent}>
                                            {instructions.leftColumn?.content}
                                        </div>
                                    </div>
                                    <div className={styles.medicationRight}>
                                        <div className={styles.medicationRightTitle}>{instructions.title}</div>
                                        <div className={styles.medicationRightContent}>{instructions.content}</div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className={styles.instructionsHeader}>{instructions.title}</div>
                                    <div className={styles.instructionsContent}>{instructions.content}</div>
                                </>
                            )}
                        </div>
                    </>
                )}
                {showDeleteModal && !isReadOnly && (
                    <GenericDeleteModal
                        title={<span className={styles.title}>Delete Task</span>}
                        body={
                            <span className={styles.body}>
                                Are you sure you want to <strong className={styles.strong}> delete </strong> this Task?
                            </span>
                        }
                        onConfirm={deleteTask}
                        onCancel={() => setShowDeleteModal(false)}
                        isLoading={isLoadingActions}
                    />
                )}
            </div>
        </div>
    );
};
